import {
  Button,
  Dropdown,
  NavItemBase,
  Tooltip,
  TooltipTrigger,
  useBreakpoint,
} from "@light-technology/components";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  AlertCircle,
  BookOpen01,
  ChevronDown,
  Code02,
  Home02,
  Lightning01,
  LogOut01,
  Menu01,
  Palette,
  Settings01,
  Stars01,
  User01,
  UserPlus01,
  Users01,
  XClose,
} from "@untitledui/icons";
import { useLayoutEffect, useRef, useState } from "react";
import { MenuItem } from "react-aria-components";
import { Outlet, useLocation, useNavigate } from "react-router";
import { isPreserveScrollState } from "../utilities/links";
import logo from "../assets/logo.svg";
import { api } from "../state/api";
import { FeatureFlag, Role, type AppResponse } from "../state/api.types";
import { chat } from "../state/chat";
import { useAuthHasFeatureFlag } from "../state/hooks";
import { useTypedDispatch } from "../state/store";
import { AppLayoutSwitcher } from "./AppLayoutSwitcher";
import { ChatSlideout } from "./chat/ChatSlideout";
import { RoleGate } from "./RoleGate";

type AppLayoutProps = {
  app?: AppResponse;
  apps: AppResponse[];
};

type NavWarningBadgeProps = {
  title: string;
};

const NavWarningBadge = ({ title }: NavWarningBadgeProps) => (
  <Tooltip title={<div className="w-52">{title}</div>} arrow>
    <TooltipTrigger>
      <AlertCircle className="size-4 text-fg-warning-secondary" />
    </TooltipTrigger>
  </Tooltip>
);

export const AppLayout = (props: AppLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [logout] = api.useLogoutMutation();

  const dispatch = useTypedDispatch();
  const isMd = useBreakpoint("md");
  const [isMdOpen, setIsMdOpen] = useState(false);
  const hasChat = useAuthHasFeatureFlag(FeatureFlag.Chat);

  // Reset scroll position when route changes, unless an overlay opened over it
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (scrollContainerRef.current && !isPreserveScrollState(location.state)) {
      scrollContainerRef.current.scrollTo(0, 0);
    }
  }, [location.pathname, location.state]);

  // Show warning if essential appearance configs haven't been set up
  const appConfig = props.app?.configuration;
  const needsAppearance =
    appConfig &&
    (!appConfig.company_name ||
      appConfig.company_name === "Light Customer" ||
      !appConfig.logo ||
      !appConfig.favicon_logo);
  const appearanceBadge = needsAppearance ? (
    <NavWarningBadge title="Add a brand name, logo, and favicon to help customers recognize your app." />
  ) : null;

  // Show warning if neither the no-code web app nor an API key is set up
  const getStandalone = api.useGetStandaloneQuery(props.app?.uuid ?? skipToken);
  const getApiKeys = api.useGetApiKeysQuery(props.app?.uuid ?? skipToken);
  const needsDevelopment =
    getStandalone.isSuccess &&
    !getStandalone.data &&
    getApiKeys.isSuccess &&
    getApiKeys.data.data.length === 0;
  const developmentBadge = needsDevelopment ? (
    <NavWarningBadge title="In order to launch, configure an app token to use the API or have us configure a no-code web app." />
  ) : null;

  return (
    <div
      className={
        isMd
          ? "flex size-full flex-row items-stretch overflow-clip"
          : "flex size-full flex-col items-stretch overflow-clip"
      }
    >
      {!isMd && (
        <div className="flex flex-none items-center justify-between gap-md border-b border-secondary p-lg">
          <img src={logo} alt="logo" className="max-h-8" />
          <Button
            color="tertiary"
            iconLeading={isMdOpen ? XClose : Menu01}
            onClick={() => {
              setIsMdOpen(!isMdOpen);
            }}
          />
        </div>
      )}
      <div
        className={
          isMd
            ? "relative flex w-74 flex-none flex-col border-r border-secondary"
            : isMdOpen
              ? "relative flex flex-auto flex-col"
              : "hidden"
        }
      >
        {props.app && (
          <div className="px-xl py-3xl">
            <AppLayoutSwitcher
              className="mb-xl"
              app={props.app}
              apps={props.apps}
            />
            <RoleGate roles={[Role.Developer]}>
              <NavItemBase
                type="link"
                href={`/apps/${props.app.slug}`}
                current={location.pathname === `/apps/${props.app.slug}`}
                icon={Home02}
              >
                Home
              </NavItemBase>
            </RoleGate>
            <NavItemBase
              type="link"
              href={`/apps/${props.app.slug}/accounts`}
              current={location.pathname.startsWith(
                `/apps/${props.app.slug}/accounts`,
              )}
              icon={Users01}
            >
              Accounts
            </NavItemBase>
            <RoleGate roles={[Role.Developer]}>
              <NavItemBase
                type="link"
                href={`/apps/${props.app.slug}/plans`}
                current={location.pathname.startsWith(
                  `/apps/${props.app.slug}/plans`,
                )}
                icon={Lightning01}
              >
                Offerings
              </NavItemBase>
              <NavItemBase
                type="link"
                href={`/apps/${props.app.slug}/appearance`}
                current={location.pathname.startsWith(
                  `/apps/${props.app.slug}/appearance`,
                )}
                icon={Palette}
                badge={appearanceBadge}
              >
                Appearance
              </NavItemBase>
              <NavItemBase
                type="link"
                href={`/apps/${props.app.slug}/development`}
                current={location.pathname.startsWith(
                  `/apps/${props.app.slug}/development`,
                )}
                icon={Code02}
                badge={developmentBadge}
              >
                Development
              </NavItemBase>
            </RoleGate>
          </div>
        )}
        <div className="mt-auto border-t border-secondary px-xl py-3xl">
          <div className={hasChat ? "relative min-w-0" : "w-full"}>
            <NavItemBase
              type="link"
              href="/playbook"
              current={location.pathname.startsWith("/playbook")}
              icon={BookOpen01}
            >
              Playbook
            </NavItemBase>
            {hasChat && (
              <div className="absolute top-0 right-0 z-1 py-2 pr-3">
                <Button
                  color="secondary"
                  size="xs"
                  iconLeading={<Stars01 className="size-3" />}
                  data-ph-capture-attribute-action="playbook-nav-ask"
                  onClick={() => {
                    dispatch(chat.actions.open());
                  }}
                >
                  Ask
                </Button>
              </div>
            )}
          </div>
          <RoleGate roles={[Role.OrgAdmin]}>
            <NavItemBase type="link" href="/settings/team" icon={UserPlus01}>
              Invite teammates
            </NavItemBase>
          </RoleGate>
          <Dropdown.Root>
            <NavItemBase
              type="link"
              current={location.pathname.startsWith("/settings")}
              badge={<ChevronDown className="size-4 text-fg-quaternary" />}
              icon={Settings01}
            >
              Settings
            </NavItemBase>
            <Dropdown.Popover placement={isMd ? "end" : "top"}>
              <Dropdown.Menu className="p-sm">
                <MenuItem>
                  <NavItemBase
                    type="link"
                    href="/settings/personal"
                    current={location.pathname.startsWith("/settings/personal")}
                    icon={User01}
                  >
                    Personal
                  </NavItemBase>
                </MenuItem>
                <MenuItem>
                  <NavItemBase
                    type="link"
                    href="/settings/team"
                    current={location.pathname.startsWith("/settings/team")}
                    icon={Users01}
                  >
                    Team
                  </NavItemBase>
                </MenuItem>
                <MenuItem>
                  <NavItemBase
                    type="link"
                    onClick={() => {
                      logout(null).then(() => {
                        navigate("/login");
                      });
                    }}
                    icon={LogOut01}
                  >
                    Sign out
                  </NavItemBase>
                </MenuItem>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown.Root>
          {isMd && (
            <div className="flex items-center justify-center gap-md pt-xl">
              <div className="text-md text-secondary">Powered by</div>
              <img src={logo} alt="logo" className="max-h-6" />
            </div>
          )}
        </div>
        {hasChat && <ChatSlideout />}
      </div>
      <div
        ref={scrollContainerRef}
        className={
          !isMd && isMdOpen
            ? "hidden"
            : "flex-auto overflow-x-hidden overflow-y-auto scroll-smooth"
        }
      >
        <Outlet />
      </div>
    </div>
  );
};
